import type { ConsentComponents, ConsentPaths, UseQueryFunctionOptions } from '../../types'
import type { Brand } from '../../types/clients/consent'
import { useUserInfo, useMutation, useQuery, useOpenapi, useCoreConfig } from '#imports'

/**
 * @title useConsentClient
 * @see Docs https://consent-prod.spilnu.dk/api/documentation
 */
export const useConsentClient = () => {
    const { site } = useCoreConfig()

    const { isLoggedIn } = useUserInfo()

    const client = useOpenapi<ConsentPaths>({
        // dedoc/scramble omits the `/api` prefix from path types (l5-swagger kept it), so it lives here.
        baseUrl: '/gateway/consent/api',
        service: 'consent'
    })

    function getShouldShowBrandedConsent(options: Omit<UseQueryFunctionOptions<ConsentPaths['/public/consent/should-show-split-consent-text']['get']['responses']['200']['content']['application/json']>, 'key'> = {}) {
        return useQuery<ConsentComponents['schemas']['ShouldShowSplitConsentTextResponse']>(async ({ signal }) => {
            const { data } = await client.GET('/public/consent/should-show-split-consent-text', { signal })

            return data!
        }, {
            ...options,
            key: 'consent:consentShouldShowBrandedConsent',
        })
    }

    function getConsent(options: Omit<UseQueryFunctionOptions<ConsentPaths['/public/consent']['get']['responses']['200']['content']['application/json']>, 'key'> = {}) {
        return useQuery(async ({ signal }) => {
            const { data } = await client.GET('/public/consent', { signal })

            return data!
        }, {
            enabled: isLoggedIn,
            ...options,
            key: 'consent:consent',
        })
    }

    const updateConsent = useMutation(async (body: ConsentComponents['schemas']['ConsentRequest']) => {
        const { data } = await client.PUT('/public/consent', {
            body: {
                ...body,
                brand: site.brand as unknown as Brand
            }
        })

        return data
    })

    return {
        client,

        getShouldShowBrandedConsent,
        getConsent,
        updateConsent,
    }
}
